import axios from 'axios';
import mimeTypes from 'mime-types';
import { call, put, takeEvery, takeLatest, retry, all, take } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';

import getVideoDuration from 'components/common/UploadModal/utils/getVideoDuration';
import { restClient } from 'config/store/configureStore';
import { MEDIAS_API_ZIP } from 'constants/ApiConstants';
import { simpleRequestsLoader } from 'modules/app/utils';
import { takeUploadItemsFn } from 'modules/mediaLibrary/saga';
import simpleRequests from 'modules/medias/requestsConfig';
import { createNotification } from 'modules/toast-notifications/actions';
import { downloadFile, downloadURL } from 'services/Utils';

import * as actions from './actions';
import { getTemplateFailure, getTemplateSuccess } from './actions';
import * as actionTypes from './actionTypes';
import * as mediaLibraryActions from '../mediaLibrary/actions';
import * as mediaLibraryActionTypes from '../mediaLibrary/actionTypes';

const RETRY_NUMBER_TIMES = 10;
const RETRY_DELAY = 2000;

function* inProgressNotify() {
  yield put(
    createNotification({
      type: 'info',
      message: {
        id: 'page.fiche.medias.zip.started',
        defaultMessage: 'Zip generation in progress, please wait...',
      },
    })
  );
}

function* afterGenerateZip({ payload: { zipId, ...response } }) {
  yield put(actions.getZip(MEDIAS_API_ZIP, zipId, response));
}

function* fetchGeneratedZip({ payload }) {
  const { request } = payload;
  const response = yield call(restClient.request, request);
  if (response.status !== 200) {
    throw new Error('Wrong status!');
  }
  return response.data;
}

function* getZip(action) {
  try {
    const zipPath = yield retry(RETRY_NUMBER_TIMES, RETRY_DELAY, fetchGeneratedZip, action);
    yield put(actions.getZipSuccess(zipPath));
  } catch (error) {
    actions.getZipFailure(error);
  }
}

const isMediaValid = ({ data, status }) => (data?.type === 'IMAGE' ? status === 200 : [200, 202].includes(status));

function* fetchMediaThumbnail(url) {
  const response = yield call(restClient.request, { method: 'GET', url });
  if (!isMediaValid(response)) {
    throw new Error('Wrong status!');
  }
  return response;
}

const getMediaIdResponse = response => {
  const uploadId = response.data.fields.key.split('/').pop();
  const [mediaId] = uploadId.split('.');
  return mediaId;
};

const getUploadIdResponse = response => response.data.fields.key.split('/').pop();

/**
 *
 * @param response
 * @param {File} file
 * @returns {Generator<*, {filename, uploadId: (*), thumbnailPath: *, type: *}|(*&{path: *, thumbnailPath: *, originalPath}), *>}
 */
function* generateTmpMedia(response, file) {
  const thumbnailPath = yield call(URL.createObjectURL, file);
  const mediaId = yield call(getMediaIdResponse, response);
  const uploadId = yield call(getUploadIdResponse, response);

  const type = response.data.type ?? response.data.fields?.['X-Amz-Meta-Type'];
  const time = file.time ?? (yield call(getVideoDuration, file));

  return {
    type,
    uploadId,
    thumbnailPath,
    time,
    size: file.size,
    path: thumbnailPath,
    filename: file.name,
    id: mediaId ?? uploadId,
    originalPath: response.data.path ?? `${response.data.url}/${response.data.fields.key}`,
  };
}

function* uploadInTwoSteps({ payload, meta }) {
  const { request, file } = payload;
  const { validate, fetchThumbnail = true } = meta;

  try {
    validate?.(file);
    const url = request.url.match(/url$/i) ? request.url : `${request.url}/url`;
    const s3Request = { ...request, url };
    const response = yield call(restClient.request, s3Request);
    const isTemporaryMedia = response.data.uploadId || response.data.fields?.key?.startsWith('temporary/');

    const uploadRequest = {
      method: 'POST',
      url: response.data.url,
      data: {
        ...response.data.fields,
        file,
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    yield call(axios.request, uploadRequest);
    if (!isTemporaryMedia && fetchThumbnail) {
      const mediaId = yield call(getMediaIdResponse, response);
      const media = yield retry(RETRY_NUMBER_TIMES, RETRY_DELAY, fetchMediaThumbnail, `${request.url}/${mediaId}`);
      yield put(actions.uploadInTwoStepsSuccess(media.data, meta));
    } else {
      // Generate "thumbnail" on our side
      const tmpMedia = yield call(generateTmpMedia, response, file);
      yield put(actions.uploadInTwoStepsSuccess(tmpMedia, meta));
    }
  } catch (error) {
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: error.message,
          defaultMessage: 'An error occurred',
        },
      })
    );
    yield put(actions.uploadInTwoStepsFailure(error, meta));
  }
}

function* applyTransformation({ payload, meta = {} }) {
  const { request } = payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(actions.applyTransformationSuccess(response.data, meta));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.fiche.notification.generate_clipping.success',
          defaultMessage: 'Clipping in progress. This operation may take a few minutes.',
        },
      })
    );
  } catch (error) {
    yield put(actions.applyTransformationFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.notification.generate_clipping.failure',
          defaultMessage: "Can't generate clipping for the media",
        },
      })
    );
  }
}

function* uploadMedias(files, baseUrl, params = {}, meta = {}) {
  if (files.length === 0) {
    return [];
  }

  yield all(
    files.map(({ file, id }) =>
      put(actions.uploadInTwoSteps({ url: baseUrl, file, params }, { ...meta, fileUUID: id })))
  );

  const uploadedMediasResult = yield all(
    files.map(({ id }) =>
      take(takeUploadItemsFn(id, [actionTypes.UPLOAD_TWO_STEPS_SUCCESS, actionTypes.UPLOAD_TWO_STEPS_FAILURE])))
  );

  const order = files.map((file) => file.id);
  return uploadedMediasResult
    .toSorted((a, b) => order.indexOf(a.meta.fileUUID) - order.indexOf(b.fileUUID))
    .map(action => {
      const { fileUUID } = action.meta;
      const uploadedFile = files.find(({ id }) => id === fileUUID);

      if (action.type === actionTypes.UPLOAD_TWO_STEPS_SUCCESS) {
        return { ...action.payload, preRecorded: uploadedFile?.preRecorded, isUploaded: true, idBeforeUpload: fileUUID };
      }
      return { ...uploadedFile, isUploaded: false };
    });
}

function* addToMediaLibrary(files, params, meta = {}) {
  if (files.length === 0) {
    return [];
  }

  yield put(mediaLibraryActions.uploadItems(files, params, meta));
  const mediaLibraryUploadedResult = yield take([
    mediaLibraryActionTypes.UPLOAD_MEDIA_LIBRARY_ITEMS_SUCCESS,
    mediaLibraryActionTypes.UPLOAD_MEDIA_LIBRARY_ITEMS_FAILURE,
  ]);
  return mediaLibraryUploadedResult.payload;
}

function* persistMediasToEntity(url, data, params, meta) {
  yield put(
    actions.persistItemsToEntity(
      {
        url,
        data,
        params
      },
      meta,
    )
  );

  return yield take([
    actionTypes.PERSIST_ITEMS_TO_ENTITY_SUCCESS,
    actionTypes.PERSIST_ITEMS_TO_ENTITY_FAILURE,
  ]);
}

const formatMediaLibraryItemsToLinks = (mediaLibraryItems, type) => mediaLibraryItems.map(({ id }) => ({ id, type }));
const formatMediasToPersist = (medias, additionalData) =>
  medias.map(({ id, filename, uploadId, media, isMediaLibraryItem, type, preRecorded }) => ({
    [isMediaLibraryItem ? 'mediaLibraryItemId' : 'uploadId']: isMediaLibraryItem ? id : uploadId,
    mediaType: type ?? media?.type,
    filename,
    preRecorded,
    ...additionalData
  }));
const markAsUploaded = (media) => ({ ...media, isUploaded: true });
const markAsNotUploaded = (media) => ({ ...media, isUploaded: false });

function* uploadAndSaveToMediaLibrary(action) {
  const { payload, meta } = action;
  const { baseUrl, medias, uploadParams, params } = payload;

  const filesToUpload = medias.filter(media => !media.isMediaLibraryItem);
  const existingMediaLibraryItems = medias.filter(media => media.isMediaLibraryItem);

  const filesToUploadInML = filesToUpload.filter(media => media.addToMediaLibrary);
  const filesToUploadOnEntity = filesToUpload.filter(media => !media.addToMediaLibrary);

  const uploadedMedias = yield call(uploadMedias, filesToUploadOnEntity, baseUrl, uploadParams, meta);
  const newMediaLibraryItems = yield call(addToMediaLibrary, filesToUploadInML, params, meta);

  return {
    uploadedMedias,
    mediasLibraryItems: [...newMediaLibraryItems, ...existingMediaLibraryItems.map(markAsUploaded)],
  };
}

/**
 * Upload medias and potentially add them in media library.
 * Persist them into the entity.
 * return all persisted medias (uploaded + media library) in the property 'uploadedMedias'
 * return all medias library (existing + added) in the property 'linkedMediaLibraryItems'
 */
function* uploadAndPersist(action) {
  const { payload, meta } = action;
  const { baseUrl, uploadData, uploadParams, additionalData } = payload;

  const { uploadedMedias, mediasLibraryItems } = yield call(uploadAndSaveToMediaLibrary, action);

  const typedMediasLibraryItems = mediasLibraryItems.map(media => ({
    ...media,
    isMediaLibraryItem: true,
    // We force the `type` of the (existing) library items to match the upload params
    // This is useful epecially for the intervention media ('Quotations and other attachments')
    type: uploadParams?.type ?? media.type ?? media.mediaType ?? media.media?.type,
    ...(media.media && {
      media: {
        ...media.media,
        type: uploadParams?.type ?? media.type ?? media.mediaType ?? media.media?.type,
      }
    })
  }));

  const mediasToPersist = [
    ...uploadedMedias,
    ...typedMediasLibraryItems
  ];

  const { error, payload: persistResponse } = yield call(
    persistMediasToEntity,
    baseUrl,
    { ...uploadData, medias: formatMediasToPersist(mediasToPersist, additionalData) },
    undefined,
    { ...meta, additionalData },
  );

  const isSuccess = !error && mediasToPersist.every(({ isUploaded }) => isUploaded);
  if (isSuccess) {
    // Merge medias to retrieve the real media id that is persisted in the entity
    // Thus we will be able to delete the media before reloading the page
    const persistedMedias = persistResponse.content.map((persistedMedia) => {
      const beforeUploadMedia = mediasToPersist.find((mediaToPersist) => {
        // Media uploaded and not linked to media library
        if (persistedMedia.uploadId) {
          return mediaToPersist.uploadId === persistedMedia.uploadId;
        }
        // Media that comes from media library or uploaded and linked to media library
        return mediaToPersist.mediaLibraryItemId === persistedMedia.mediaLibraryItemId ||
          mediaToPersist.id === persistedMedia.mediaLibraryItemId;
      });

      return {
        ...markAsUploaded(beforeUploadMedia.media ?? beforeUploadMedia),
        ...persistedMedia,
        ...additionalData,
        uploadId: undefined,
        mediaLibraryItemId: undefined
      };
    });

    yield put(actions.uploadMediasSuccess({
      uploadedMedias: persistedMedias,
      linkedMediaLibraryItems: typedMediasLibraryItems
    }, meta));
  } else {
    yield put(actions.uploadMediasFailure({
      uploadedMedias: mediasToPersist,
      linkedMediaLibraryItems: mediasLibraryItems
    }, meta));
  }
}

/**
 * Upload medias and potentially add them in media library.
 * Link medias from the media library (existing + added) into the entity
 *
 * In case of success :
 * return all medias (uploaded + media library) in the property 'uploadedMedias'
 * return all medias library (existing + added) in the property 'linkedMediaLibraryItems'
 *
 * In case of error :
 * return all uploaded medias in the property 'uploadedMedias' some of them may be not uploaded
 * return all medias library (existing + added) in the property 'linkedMediaLibraryItems' all of them as mark as not uploaded
 */
function* uploadAndLink(action) {
  const { payload, meta } = action;
  const { linkURL, uploadParams } = payload;

  const { uploadedMedias, mediasLibraryItems } = yield call(uploadAndSaveToMediaLibrary, action);

  const mediasToLink = mediasLibraryItems.filter(({ isUploaded }) => isUploaded);
  if (linkURL && mediasToLink.length > 0) {
    const idsToLink = formatMediaLibraryItemsToLinks(mediasToLink, uploadParams?.type);
    const { error, payload: linkResponse } = yield call(persistMediasToEntity, linkURL, { items: idsToLink }, uploadParams, meta);

    const isSuccess = !error && [...uploadedMedias, ...mediasLibraryItems].every(({ isUploaded }) => isUploaded);
    if (isSuccess) {
      return yield put(actions.uploadMediasSuccess({
        uploadedMedias: [...uploadedMedias, ...linkResponse.map(markAsUploaded)],
        linkedMediaLibraryItems: mediasLibraryItems.map(markAsUploaded)
      }, meta));
    }
  }

  const isSuccess = uploadedMedias.every(({ isUploaded }) => isUploaded);
  if (isSuccess) {
    return yield put(actions.uploadMediasSuccess({
      uploadedMedias,
      linkedMediaLibraryItems: mediasLibraryItems
    }, meta));
  }

  return yield put(actions.uploadMediasFailure({
    uploadedMedias,
    linkedMediaLibraryItems: mediasLibraryItems.map(markAsNotUploaded)
  }, meta));
}

/**
 * Upload medias and potentially add them in media library.
 * The existing media library item or the potential one that that has been added will be return in the uploaded medias.
 *
 * return all medias (uploaded + media library) in the property 'uploadedMedias'
 * return all medias library (existing + added) in the property 'linkedMediaLibraryItems'
 */
function* temporaryUpload(action) {
  const { meta } = action;
  const { uploadedMedias, mediasLibraryItems } = yield call(uploadAndSaveToMediaLibrary, action);

  const isSuccess = [...uploadedMedias, ...mediasLibraryItems].every(({ isUploaded }) => isUploaded);
  if (isSuccess) {
    const linkedMediaLibraryItems = mediasLibraryItems.map(({ media, id: mediaLibraryItemId }) => ({
      ...markAsUploaded(media),
      id: uuidv4(),
      mediaLibraryItemId,
    }));
    yield put(actions.uploadMediasSuccess({
      uploadedMedias: [...uploadedMedias, ...linkedMediaLibraryItems],
      linkedMediaLibraryItems,
    }, meta));
  } else {
    const linkedMediaLibraryItems = mediasLibraryItems.map(markAsNotUploaded);
    yield put(actions.uploadMediasFailure({
      uploadedMedias: [...uploadedMedias, ...linkedMediaLibraryItems],
      linkedMediaLibraryItems,
    }, meta));
  }
}

function* downloadMedia({ payload, meta = {} }) {
  const { url, filename } = payload;
  try {
    const response = yield call(fetch, url, { cache: 'no-store' });
    const name = response.headers.get('content-disposition')?.match(/filename=['"](.*)['"]/)?.[1];
    const blob = yield call([response, 'blob']);
    const fileUrl = URL.createObjectURL(blob);

    downloadURL(fileUrl, filename ?? name);
    URL.revokeObjectURL(fileUrl);
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.fiche.medias.download_started',
          defaultMessage: 'Download started',
        },
      })
    );
    yield put(actions.downloadFetchedURLSuccess(meta));
  } catch (error) {
    yield put(actions.downloadFetchedURLFailure(error, meta));
    yield put(createNotification({
      type: 'error',
      message: {
        id: error.message,
        defaultMessage: 'An error occurred',
      },
    }));
  }
}

const isUploadAction = ({ type }) => type === actionTypes.UPLOAD_MEDIAS;
const shouldTemporaryUpload = (action) => isUploadAction(action) && Boolean(action.payload.temporary);
const shouldUploadAndPersist = (action) => isUploadAction(action) && Boolean(action.payload.persistAtUpload);
const shouldUploadAndLink = (action) => isUploadAction(action) && !action.payload.temporary && !action.payload.persistAtUpload;

function* getTemplate({ payload }) {
  const { params, request } = payload;

  try {
    const { data: templateFile } = yield call(restClient.request, request);
    const fileName = `template.${params.extension}`;
    const type = mimeTypes.lookup(fileName);
    if (request.responseType === 'arraybuffer') {
      const blob = new Blob([templateFile], { type });
      const url = URL.createObjectURL(blob);
      yield call(downloadURL, url, fileName);
    } else {
      const b64File = btoa(
        new TextEncoder().encode(templateFile).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );
      yield call(downloadFile, b64File, { fileName, type });
    }
    yield put(getTemplateSuccess(templateFile));
  } catch (error) {
    yield put(getTemplateFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.contactLists.details.contactMember.add.template.notification.failure',
          defaultMessage: 'An error occurred while downloading the template.',
        },
      })
    );
  }
}

const archivedMediasSimpleRequests = {
  [actionTypes.DEFROST_MEDIAS_REQUEST]: {
    notifications: {
      success: {
        id: 'page.fiche.medias.defrost.notification.success',
        defaultMessage: 'Request sent! The media will be available again within a maximum of 12 hours.',
      }
    },
    callbacks: {
      success: ({ mediaIds }, meta) => actions.defrostMediaSuccess(mediaIds, meta),
    }
  }
};

export default function* mediaSagas() {
  yield takeEvery(actionTypes.GENERATE_ZIP, inProgressNotify);
  yield takeEvery(actionTypes.GENERATE_ZIP_SUCCESS, afterGenerateZip);
  yield takeEvery(actionTypes.GET_ZIP, getZip);
  yield takeEvery(actionTypes.GET_ZIP_SUCCESS, downloadMedia);
  yield takeEvery(actionTypes.UPLOAD_TWO_STEPS, uploadInTwoSteps);
  yield takeLatest(actionTypes.APPLY_TRANSFORMATIONS, applyTransformation);
  yield takeLatest(actionTypes.DOWNLOAD_MEDIA, downloadMedia);
  yield takeEvery(actionTypes.GET_TEMPLATE, getTemplate);

  yield takeLatest(shouldUploadAndLink, uploadAndLink);
  yield takeLatest(shouldTemporaryUpload, temporaryUpload);
  yield takeLatest(shouldUploadAndPersist, uploadAndPersist);

  yield all(simpleRequestsLoader(simpleRequests));
  yield all(simpleRequestsLoader(archivedMediasSimpleRequests));
}
